import type { MvpEntryMode } from "@/hooks/use-mvp-persistence";
import styles from "@/components/mvp/EntryModeCards.module.css";

export type EntryModeCardsProps = {
  value: MvpEntryMode | null;
  submitting?: boolean;
  onChange: (value: MvpEntryMode) => void;
};

const ENTRY_MODES: Array<{
  value: MvpEntryMode;
  title: string;
  description: string;
  tag: string;
  action: string;
  Illustration: () => JSX.Element;
}> = [
  {
    value: "packages",
    title: "Pakiety robocze",
    description:
      "Zaznacz typy prac i pomieszczenia - Builder wygeneruje pozycje kosztorysu automatycznie. Możesz je edytować w następnym kroku.",
    tag: "Szybciej przy pierwszym użyciu",
    action: "Wybierz pakiety →",
    Illustration: PackagesIllustration,
  },
  {
    value: "manual",
    title: "Wprowadzanie ręczne",
    description:
      "Zacznij od pustej tabeli i dodaj pozycje z bazy stawek lub wpisz własne. Pełna kontrola od razu.",
    tag: "Dla zaawansowanych",
    action: "Zacznij od zera →",
    Illustration: ManualIllustration,
  },
];

export function EntryModeCards({ value, submitting = false, onChange }: EntryModeCardsProps) {
  return (
    <div className={styles.cards} role="radiogroup" aria-label="Tryb wprowadzania pozycji">
      {ENTRY_MODES.map(({ value: cardValue, title, description, tag, action, Illustration }) => {
        const isActive = value === cardValue;
        return (
          <button
            key={cardValue}
            type="button"
            role="radio"
            aria-checked={isActive}
            data-selected={isActive}
            className={styles.card}
            disabled={submitting}
            onClick={() => onChange(cardValue)}
          >
            <span className={styles.illustration} aria-hidden>
              <Illustration />
            </span>
            <span className={styles.content}>
              <span className={styles.tag}>{tag}</span>
              <span className={styles.title}>{title}</span>
              <span className={styles.description}>{description}</span>
            </span>
            <span className={styles.action}>{submitting && isActive ? "Tworzenie..." : action}</span>
          </button>
        );
      })}
    </div>
  );
}

function PackagesIllustration() {
  return (
    <svg viewBox="0 0 320 180" fill="none" xmlns="http://www.w3.org/2000/svg" role="img">
      <rect x="18" y="18" width="132" height="118" rx="10" fill="#F2EDE3" stroke="#14120D" strokeWidth="3" />
      {[
        ["Malowanie", 43],
        ["Gładzie", 75],
        ["Płytki", 107],
      ].map(([label, y]) => (
        <g key={label}>
          <rect x="36" y={Number(y) - 13} width="22" height="22" rx="4" fill="#FFF8EC" stroke="#14120D" strokeWidth="2" />
          <path d={`M40 ${Number(y) - 1}l5 5 10-13`} stroke="#C7541F" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
          <text x="68" y={Number(y) + 5} fill="#14120D" fontFamily="Arial, sans-serif" fontSize="17" fontWeight="700">
            {label}
          </text>
        </g>
      ))}
      <path d="M166 74h46" stroke="#C7541F" strokeWidth="5" strokeLinecap="round" />
      <path d="M204 58l18 18-18 18" stroke="#C7541F" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="226" y="42" width="76" height="94" rx="8" fill="#FFF8EC" stroke="#14120D" strokeWidth="3" />
      <path d="M226 68h76M226 94h76M226 120h76M252 42v94" stroke="#14120D" strokeWidth="2" />
      <path d="M238 55h52M238 81h24M264 81h26M238 107h24M264 107h26" stroke="#C7541F" strokeWidth="3" strokeLinecap="round" />
      <path d="M20 152h282" stroke="#14120D" strokeWidth="3" strokeLinecap="round" opacity="0.18" />
    </svg>
  );
}

function ManualIllustration() {
  return (
    <svg viewBox="0 0 320 180" fill="none" xmlns="http://www.w3.org/2000/svg" role="img">
      <rect x="28" y="35" width="264" height="108" rx="10" fill="#FFF8EC" stroke="#14120D" strokeWidth="3" />
      <path d="M28 68h264M28 101h264M132 35v108M198 35v108M250 35v108" stroke="#14120D" strokeWidth="2" />
      <text x="42" y="57" fill="#14120D" fontFamily="Arial, sans-serif" fontSize="13" fontWeight="700">
        Pozycja
      </text>
      <text x="146" y="57" fill="#14120D" fontFamily="Arial, sans-serif" fontSize="13" fontWeight="700">
        Ilosc
      </text>
      <text x="210" y="57" fill="#14120D" fontFamily="Arial, sans-serif" fontSize="13" fontWeight="700">
        Cena
      </text>
      <text x="262" y="57" fill="#14120D" fontFamily="Arial, sans-serif" fontSize="13" fontWeight="700">
        Razem
      </text>
      <text x="42" y="89" fill="#14120D" fontFamily="Arial, sans-serif" fontSize="13" fontWeight="700">
        Malowanie ścian
      </text>
      <text x="146" y="89" fill="#14120D" fontFamily="Arial, sans-serif" fontSize="13" fontWeight="700">
        80 m²
      </text>
      <text x="210" y="89" fill="#14120D" fontFamily="Arial, sans-serif" fontSize="13" fontWeight="700">
        35 zł
      </text>
      <text x="262" y="89" fill="#14120D" fontFamily="Arial, sans-serif" fontSize="13" fontWeight="700">
        2800
      </text>
      <path d="M126 76v18" stroke="#C7541F" strokeWidth="3" strokeLinecap="round">
        <animate attributeName="opacity" values="1;0;1" dur="1s" repeatCount="indefinite" />
      </path>
      <path d="M42 121h62M146 121h34M210 121h25M262 121h18" stroke="#C7541F" strokeWidth="3" strokeLinecap="round" opacity="0.65" />
      <path d="M28 158h264" stroke="#14120D" strokeWidth="3" strokeLinecap="round" opacity="0.18" />
    </svg>
  );
}
